import { Injectable, inject, PLATFORM_ID, TransferState, makeStateKey } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { Observable, of, map, catchError, tap } from 'rxjs';
import { ServicioContenidoModyo } from '@core/services/modyo/modyo-contenido.service';
import type { PaginaConRuta, EntradaPaginaModyo } from './routing-dinamico.types';

const LONGITUD_MINIMA_ENTRADAS = 0;
const INDICE_PRIMERA_ENTRADA = 0;

@Injectable({ providedIn: 'root' })
export class ServicioRoutingDinamico {
  private readonly servicioModyo = inject(ServicioContenidoModyo);
  private readonly transferState = inject(TransferState);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly esServidor = isPlatformServer(this.platformId);
  private readonly cachePaginas = new Map<string, PaginaConRuta>();

  public obtenerPaginaPorRuta(ruta: string): Observable<PaginaConRuta | null> {
    const rutaNormalizada = this.normalizarRuta(ruta);
    const claveTransferState = makeStateKey<PaginaConRuta | null>(`routing-pagina-${rutaNormalizada}`);

    const paginaEnCache = this.cachePaginas.get(rutaNormalizada);
    if (paginaEnCache) {
      return of(paginaEnCache);
    }

    if (!this.esServidor) {
      const datosEnTransferState = this.transferState.get(claveTransferState, null);
      if (datosEnTransferState) {
        this.transferState.remove(claveTransferState);
        this.guardarPaginaEnCacheLocal(rutaNormalizada, datosEnTransferState);
        return of(datosEnTransferState);
      }
    }

    return this.obtenerPaginaDesdeModyo(rutaNormalizada, claveTransferState);
  }

  public rutaExiste(ruta: string): Observable<boolean> {
    return this.obtenerPaginaPorRuta(ruta).pipe(map((pagina) => pagina !== null));
  }

  public limpiarCache(): void {
    this.cachePaginas.clear();
  }

  private obtenerPaginaDesdeModyo(
    rutaNormalizada: string,
    claveTransferState: ReturnType<typeof makeStateKey<PaginaConRuta | null>>,
  ): Observable<PaginaConRuta | null> {
    const rutaSinPrefijo = this.extraerSlugDeRuta(rutaNormalizada);

    const filtro = this.servicioModyo.crearFiltro('pagina');
    filtro['where']('ruta', 'eq', rutaSinPrefijo);

    return this.servicioModyo.obtenerEntradas<EntradaPaginaModyo>('pagina', { filter: filtro }).pipe(
      map((respuesta) => {
        if (!respuesta.entries || respuesta.entries.length === LONGITUD_MINIMA_ENTRADAS) {
          return null;
        }

        const entrada = respuesta.entries[INDICE_PRIMERA_ENTRADA];
        return this.transformarEntradaAPagina(entrada, rutaNormalizada);
      }),
      tap((pagina) => {
        if (this.esServidor && pagina) {
          this.transferState.set(claveTransferState, pagina);
        }
      }),
      catchError(() => of(null)),
    );
  }

  private transformarEntradaAPagina(entrada: EntradaPaginaModyo, rutaNormalizada: string): PaginaConRuta {
    const rutaModyo = entrada.fields.ruta ?? `/${entrada.meta.slug}`;
    const pagina: PaginaConRuta = {
      slug: entrada.meta.slug,
      ruta: rutaModyo,
      titulo: entrada.fields.titulo ?? entrada.meta.name,
      uuid: entrada.meta.uuid,
    };

    this.guardarPaginaEnCache(rutaNormalizada, pagina, entrada.meta.slug);
    return pagina;
  }

  private guardarPaginaEnCache(rutaNormalizada: string, pagina: PaginaConRuta, slug: string): void {
    this.cachePaginas.set(rutaNormalizada, pagina);
    this.cachePaginas.set(slug, pagina);
  }

  private guardarPaginaEnCacheLocal(rutaNormalizada: string, pagina: PaginaConRuta): void {
    this.cachePaginas.set(rutaNormalizada, pagina);
    this.cachePaginas.set(pagina.slug, pagina);
  }

  private normalizarRuta(ruta: string): string {
    return ruta.replace(/^\//, '').replace(/\/$/, '');
  }

  private extraerSlugDeRuta(ruta: string): string {
    const segmentos = ruta.split('/').filter(Boolean);
    const INDICE_ULTIMO_ELEMENTO = -1;
    return segmentos.at(INDICE_ULTIMO_ELEMENTO) ?? '';
  }
}
